import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Instagram, Twitter, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SocialConnect = ({ user, onUpdate }) => {
  const [showConnect, setShowConnect] = useState(false);
  const [provider, setProvider] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [instagramConfigured, setInstagramConfigured] = useState(false);

  const socialAccounts = user?.social_accounts || [];
  const hasInstagram = socialAccounts.some(acc => acc.provider === 'instagram');
  const hasTwitter = socialAccounts.some(acc => acc.provider === 'twitter');

  // Check if Instagram API is configured
  useEffect(() => {
    const checkInstagramStatus = async () => {
      try {
        const response = await axios.get(`${API}/instagram/status`);
        setInstagramConfigured(response.data.configured);
      } catch (error) {
        console.log('Instagram status check failed');
      }
    };
    checkInstagramStatus();
  }, []);

  const handleConnect = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/social/connect`, null, {
        params: { provider, username }
      });
      toast.success(`${provider === 'instagram' ? 'Instagram' : 'Twitter/X'} connected!`);
      setShowConnect(false);
      setUsername('');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async (providerName) => {
    try {
      await axios.delete(`${API}/social/disconnect/${providerName}`);
      toast.success(`${providerName === 'instagram' ? 'Instagram' : 'Twitter/X'} disconnected`);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const openConnectDialog = (providerName) => {
    setProvider(providerName);
    setShowConnect(true);
  };

  return (
    <div>
      <Card className="glass p-6 border border-slate-700">
        <h3 className="text-xl font-semibold text-white mb-4">
          Social Media Accounts
        </h3>
        <p className="text-sm text-slate-400 mb-4">
          Connect your social accounts to share check-ins and earn <span className="text-green-400 font-semibold">5-10% bonus discounts!</span>
        </p>
        
        <div className="space-y-3">
          {/* Instagram */}
          <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                <Instagram className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-white font-semibold">Instagram</p>
                  {instagramConfigured && (
                    <span className="flex items-center gap-1 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                      <CheckCircle className="w-3 h-3" /> API Active
                    </span>
                  )}
                </div>
                {hasInstagram ? (
                  <p className="text-xs text-slate-400">
                    Connected: @{socialAccounts.find(acc => acc.provider === 'instagram')?.username}
                  </p>
                ) : (
                  <p className="text-xs text-slate-400">Not connected</p>
                )}
              </div>
            </div>
            {hasInstagram ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDisconnect('instagram')}
                className="border-red-500 text-red-400 hover:bg-red-500/10"
              >
                Disconnect
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => openConnectDialog('instagram')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                Connect
              </Button>
            )}
          </div>

          {/* Twitter/X */}
          <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <Twitter className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold">Twitter/X</p>
                {hasTwitter ? (
                  <p className="text-xs text-slate-400">
                    Connected: @{socialAccounts.find(acc => acc.provider === 'twitter')?.username}
                  </p>
                ) : (
                  <p className="text-xs text-slate-400">Not connected</p>
                )}
              </div>
            </div>
            {hasTwitter ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDisconnect('twitter')}
                className="border-red-500 text-red-400 hover:bg-red-500/10"
              >
                Disconnect
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => openConnectDialog('twitter')}
                className="bg-blue-500 hover:bg-blue-600"
              >
                Connect
              </Button>
            )}
          </div>
        </div>

        <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
          <p className="text-sm text-green-400">
            💰 Bonus Rewards:
          </p>
          <ul className="text-xs text-slate-300 mt-2 space-y-1">
            <li>• Share to 1 platform = 5% extra discount + 15 points</li>
            <li>• Share to both platforms = 10% extra discount + 30 points</li>
          </ul>
        </div>
      </Card>

      {/* Connect Dialog */}
      <Dialog open={showConnect} onOpenChange={setShowConnect}>
        <DialogContent className="glass border border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              Connect {provider === 'instagram' ? 'Instagram' : 'Twitter/X'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleConnect} className="space-y-4">
            <div>
              <p className="text-sm text-slate-300 mb-3">
                {provider === 'instagram' 
                  ? 'Enter your Instagram username to connect'
                  : 'Enter your Twitter/X username to connect'
                }
              </p>
              <Input
                placeholder={provider === 'instagram' ? '@instagram_username' : '@twitter_username'}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="bg-slate-800 border-slate-700 text-white"
              />
              {provider === 'instagram' && instagramConfigured ? (
                <p className="text-xs text-green-400 mt-2">
                  ✅ Instagram API is configured and ready for sharing!
                </p>
              ) : (
                <p className="text-xs text-slate-400 mt-2">
                  {provider === 'twitter' 
                    ? '⚠️ Twitter/X API credentials needed for full integration.'
                    : '⚠️ Connect your account to enable sharing features.'
                  }
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                className={provider === 'instagram' 
                  ? 'flex-1 bg-gradient-to-r from-purple-600 to-pink-600' 
                  : 'flex-1 bg-blue-500'
                }
                disabled={loading}
              >
                {loading ? 'Connecting...' : 'Connect'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowConnect(false)}
                className="border-slate-700 text-slate-300"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialConnect;
